import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectUser } from 'redux/slices/userSlice';
import { ScaleType } from 'util/Enums';

export const CONNECT_SCALE_STORAGE_KEY = 'CONNECT_TO_SCALE';

export enum ScaleConnectionStatus {
  ON = 'on',
  OFF = 'off',
}

export const useConnectToScale = (params?: {
  scaleUsingCondition?: boolean;
}) => {
  const { t } = useTranslation();
  const { scalesType } = useSelector(selectUser);
  const { scaleUsingCondition = true } = params ?? {};

  const [isTurnedOn, setIsTurnedOn] = useState<boolean>(
    localStorage.getItem(CONNECT_SCALE_STORAGE_KEY) !==
      ScaleConnectionStatus.OFF && scalesType !== ScaleType.NONE
  );

  const updateScaleConnectionStatus = (isInitial?: boolean) => {
    if (scalesType === ScaleType.NONE) {
      setIsTurnedOn(false);

      try {
        window?.connectWithScale?.(false, scalesType);
      } catch (e) {
        // do nothing
      }

      return;
    }

    try {
      if (!isInitial) {
        window?.connectWithScale?.(!isTurnedOn, scalesType);

        localStorage.setItem(
          CONNECT_SCALE_STORAGE_KEY,
          isTurnedOn ? ScaleConnectionStatus.OFF : ScaleConnectionStatus.ON
        );
        setIsTurnedOn(!isTurnedOn);
      } else {
        window?.connectWithScale?.(
          localStorage.getItem(CONNECT_SCALE_STORAGE_KEY) !==
            ScaleConnectionStatus.OFF,
          scalesType
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e?.message?.includes?.('Android')) {
        if (!isInitial) {
          toast.error(t('emission_input.message_scale_not_supported'));
        } else {
          setIsTurnedOn(false);
        }
      } else if (e.message) {
        toast.error(e.message);
      }
    }
  };

  useEffect(() => {
    updateScaleConnectionStatus(true);
  }, [scalesType]);

  useEffect(() => {
    const handleSetScaleUsing = (isUsing: boolean) => {
      try {
        window?.setScaleUsing?.(scalesType !== ScaleType.NONE && isUsing);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        if (!e?.message?.includes?.('Android') && e.message) {
          toast.error(e.message);
        }
      }
    };

    handleSetScaleUsing(scaleUsingCondition);

    return () => {
      handleSetScaleUsing(false);
    };
  }, [scaleUsingCondition, scalesType]);

  return {
    scaleConnected: isTurnedOn,
    handleChangeScaleConnectionStatus: () => updateScaleConnectionStatus(),
  };
};
